import type { TFunction } from 'i18next';

import type {
  CustomField,
  IBlackbaudContactMapping,
  IBlackbaudContactMappingUpdate,
  TBlackbaudContactMappingKey,
  TIntegrationPersonAttributes,
} from '@feathr/blackbox';
import { defaultPersonAttributes } from '@feathr/blackbox';
import type { IIntegrationUIFieldOption, IIntegrationUIMapping } from '@feathr/components';
import { isCustomFieldDataType } from '@feathr/components';

/** Default person attributes a mapping can target. v1 leaves out the all-opt-outs flag too. */
const DEFAULT_FIELD_OPTIONS: readonly IIntegrationUIFieldOption[] = defaultPersonAttributes
  .filter(({ id }) => id !== 'pp_opt_outs.all')
  .flatMap(({ data_type: dataType, id, u_key: name }) =>
    isCustomFieldDataType(dataType) ? [{ dataType, id, name }] : [],
  );

const DEFAULT_FIELD_IDS: ReadonlySet<string> = new Set(DEFAULT_FIELD_OPTIONS.map(({ id }) => id));

/** The Feathr fields a mapping can target: the default person attributes, then the custom fields. */
export function getFeathrFieldOptions(
  customFields: readonly CustomField[],
): IIntegrationUIFieldOption[] {
  const customFieldOptions = customFields.flatMap((field) => {
    const dataType = field.get('data_type');
    return isCustomFieldDataType(dataType)
      ? [{ dataType, id: field.id, name: field.get('u_key') }]
      : [];
  });
  return [...DEFAULT_FIELD_OPTIONS, ...customFieldOptions];
}

/** The Feathr field a mapping targets, a default attribute or a custom field id. */
function getFeathrFieldKey(mapping: IBlackbaudContactMapping): string | null {
  return mapping.custom_field ?? mapping.default_field ?? null;
}

/** Why a stopped mapping needs attention. `undefined` while it syncs or after the customer turned it off. */
export function getMappingWarning(
  { status, stopped_reason: stoppedReason }: IBlackbaudContactMapping,
  t: TFunction,
): string | undefined {
  if (status !== 'stopped' || !stoppedReason) {
    return undefined;
  }
  switch (stoppedReason) {
    case 'deleted':
      return t('This field is no longer available, so the mapping stopped syncing.');

    case 'not_writable':
      return t("Feathr can't write to this field, so the mapping stopped syncing.");

    case 'custom_field_missing':
      return t('The Feathr custom field was archived or renamed, so the mapping stopped syncing.');

    default:
      return t('This mapping stopped syncing.');
  }
}

/**
 * A saved mapping as a table row. The row key is the Blackbaud field key, so each row keeps the
 * v1 `sync-mapping-<key>` e2e name.
 */
export function toUIMapping(
  mapping: IBlackbaudContactMapping,
  t: TFunction,
): IIntegrationUIMapping {
  let removeDisabledReason: string | undefined;
  if (mapping.required) {
    removeDisabledReason = t("Required, can't be removed");
  } else if (mapping.has_provisioning_subscriptions) {
    removeDisabledReason = t(
      'This mapping is being set up in Blackbaud. Try again in a few minutes.',
    );
  }

  return {
    crmFieldKey: mapping.key,
    feathrFieldKey: getFeathrFieldKey(mapping),
    key: mapping.key,
    lastSyncedAt: mapping.date_last_synced,
    removeDisabledReason,
    syncRule: mapping.sync_rule,
    warning: getMappingWarning(mapping, t),
  };
}

/** The update that points a mapping at a Feathr field, or at none. */
export function toMappingUpdate(
  mappingId: IBlackbaudContactMapping['id'],
  feathrFieldKey: string | null,
): IBlackbaudContactMappingUpdate {
  const isDefaultField = feathrFieldKey !== null && DEFAULT_FIELD_IDS.has(feathrFieldKey);
  return {
    customField: isDefaultField ? null : feathrFieldKey,
    defaultField: isDefaultField ? (feathrFieldKey as TIntegrationPersonAttributes) : null,
    mapping: mappingId,
  };
}

/** A mapping to create, and the Feathr field to point it at once it exists. */
export interface IMappingAddition {
  key: TBlackbaudContactMappingKey;
  feathrFieldKey: string | null;
}

/**
 * The requests that turn the saved mappings into the table rows. A mapping moves to a new Feathr
 * field in one update, never a clear and then an assign: a magpie-owned integration deletes a
 * mapping cleared to no field. See `orderMappingUpdates` for the order the assigns run in.
 */
export interface IMappingChanges {
  remove: Array<IBlackbaudContactMapping['id']>;
  /** Saved mappings that give up their Feathr field and take none. */
  clear: Array<IBlackbaudContactMapping['id']>;
  add: IMappingAddition[];
  /** Saved mappings that take a new Feathr field. */
  assign: IBlackbaudContactMappingUpdate[];
}

/** The follow-up requests for new mappings, which the server can create with a default field. */
export interface IAddedMappingChanges {
  clear: Array<IBlackbaudContactMapping['id']>;
  assign: IBlackbaudContactMappingUpdate[];
}

/** Blackbaud fields the server points at a Feathr field it manages when it creates the mapping. */
const SERVER_BOUND_KEYS: ReadonlySet<string> = new Set<TBlackbaudContactMappingKey>([
  'emails',
  'solicit_codes',
]);

/**
 * Locks the Feathr field of a row the integration manages: a saved mapping that isn't editable, or
 * a new one the server points at its own field. A locked row keeps the saved Feathr field.
 */
export function lockManagedFeathrFields(
  rows: readonly IIntegrationUIMapping[],
  saved: readonly IBlackbaudContactMapping[],
): IIntegrationUIMapping[] {
  const savedByKey = new Map<string, IBlackbaudContactMapping>(
    saved.map((mapping) => [mapping.key, mapping]),
  );
  return rows.map((row) => {
    const mapping = row.crmFieldKey === null ? undefined : savedByKey.get(row.crmFieldKey);
    if (mapping ? !mapping.editable : SERVER_BOUND_KEYS.has(row.crmFieldKey ?? '')) {
      return {
        ...row,
        feathrFieldKey: mapping ? getFeathrFieldKey(mapping) : null,
        isFeathrFieldLocked: true,
      };
    }
    return row.isFeathrFieldLocked ? { ...row, isFeathrFieldLocked: false } : row;
  });
}

/**
 * The requests that turn the saved mappings into the table rows. Rows match saved mappings by
 * Blackbaud field, which a mapping can't change, so a row moved to another field removes the old
 * mapping and adds a new one. Rows with no Blackbaud field yet are left out. Direction is locked
 * to Blackbaud to Feathr, so only the Feathr field is compared. Feathr fields the integration
 * manages are never changed.
 */
export function getMappingChanges(
  saved: readonly IBlackbaudContactMapping[],
  rows: readonly IIntegrationUIMapping[],
): IMappingChanges {
  const rowsByField = new Map(
    rows.flatMap((row) => (row.crmFieldKey === null ? [] : [[row.crmFieldKey, row] as const])),
  );
  const savedKeys = new Set<string>(saved.map(({ key }) => key));
  const changes: IMappingChanges = { add: [], assign: [], clear: [], remove: [] };

  saved.forEach((mapping) => {
    const row = rowsByField.get(mapping.key);
    const savedFeathrFieldKey = getFeathrFieldKey(mapping);
    if (!row) {
      changes.remove.push(mapping.id);
    } else if (mapping.editable && row.feathrFieldKey !== savedFeathrFieldKey) {
      if (row.feathrFieldKey === null) {
        changes.clear.push(mapping.id);
      } else {
        changes.assign.push(toMappingUpdate(mapping.id, row.feathrFieldKey));
      }
    }
  });
  rowsByField.forEach((row, key) => {
    if (!savedKeys.has(key)) {
      changes.add.push({
        feathrFieldKey: SERVER_BOUND_KEYS.has(key) ? null : row.feathrFieldKey,
        key: key as TBlackbaudContactMappingKey,
      });
    }
  });
  return changes;
}

/**
 * The requests that point new mappings at their staged Feathr fields. The server can create a
 * mapping with a default field; it is kept when the row has none, unless another row takes it.
 */
export function getAddedMappingChanges(
  created: readonly IBlackbaudContactMapping[],
  { add, assign }: Pick<IMappingChanges, 'add' | 'assign'>,
): IAddedMappingChanges {
  const taken = new Set<string>([
    ...assign.flatMap(({ customField, defaultField }) => customField ?? defaultField ?? []),
    ...add.flatMap(({ feathrFieldKey }) => feathrFieldKey ?? []),
  ]);
  const changes: IAddedMappingChanges = { assign: [], clear: [] };

  created.forEach((mapping) => {
    const addition = add.find(({ key }) => key === mapping.key);
    const createdFeathrFieldKey = getFeathrFieldKey(mapping);
    if (!addition || SERVER_BOUND_KEYS.has(mapping.key)) {
      return;
    }
    const target = addition.feathrFieldKey;
    if (createdFeathrFieldKey === target) {
      return;
    }
    if (target !== null) {
      changes.assign.push(toMappingUpdate(mapping.id, target));
    } else if (createdFeathrFieldKey !== null && taken.has(createdFeathrFieldKey)) {
      changes.clear.push(mapping.id);
    }
  });
  return changes;
}

/**
 * Orders the assigns so each takes its Feathr field only after the mapping holding it has moved
 * off. Feathr fields are unique per integration. Mappings that trade fields in a cycle can't be
 * ordered, so the first is cleared to break it, which deletes it on a magpie-owned integration.
 */
export function orderMappingUpdates(
  assign: readonly IBlackbaudContactMappingUpdate[],
  mappings: readonly IBlackbaudContactMapping[],
): IBlackbaudContactMappingUpdate[] {
  const heldFields = new Map(mappings.map((mapping) => [mapping.id, getFeathrFieldKey(mapping)]));
  const pending = [...assign];
  const ordered: IBlackbaudContactMappingUpdate[] = [];
  while (pending.length) {
    const held = new Set(pending.flatMap(({ mapping }) => heldFields.get(mapping) ?? []));
    const index = pending.findIndex(
      ({ customField, defaultField }) => !held.has(customField ?? defaultField ?? ''),
    );
    if (index === -1) {
      ordered.push(toMappingUpdate(pending[0].mapping, null));
      heldFields.set(pending[0].mapping, null);
    } else {
      ordered.push(...pending.splice(index, 1));
    }
  }
  return ordered;
}

export function hasMappingChanges({ add, assign, clear, remove }: IMappingChanges): boolean {
  return add.length + assign.length + clear.length + remove.length > 0;
}
